import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  Fish,
  MapPin,
  Award,
  Calendar,
  Snowflake,
  ShieldCheck,
  ExternalLink,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';
import DemoBanner from '../../components/optimar/DemoBanner';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BATCH_ID = 'OPT-DEMO-001';

const InfoLine = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 rounded-lg border border-slate-100 bg-white p-3">
    <div className="mt-0.5 rounded-md bg-slate-100 p-2">
      <Icon className="h-4 w-4 text-slate-700" />
    </div>
    <div>
      <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
      <p className="text-sm font-medium text-slate-900">{value}</p>
    </div>
  </div>
);

const OptimarCustomerView = () => {
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(
          `${BACKEND_URL}/api/optimar/batches/${BATCH_ID}/customer-view`
        );
        setView(res.data);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load customer view');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
          <p className="mt-4 text-slate-600">Loading customer view…</p>
        </div>
      </div>
    );
  }

  if (!view) return null;

  const verifyPath = view.verify_url_path || `/verify/${BATCH_ID}`;
  const verifyUrl = `${window.location.origin}${verifyPath}`;
  const productionDate = new Date(view.production_date).toLocaleDateString('en-NO', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const resolved = !!view.traceability_resolved;

  return (
    <div className="space-y-8" data-testid="optimar-customer-view-page">
      <DemoBanner />

      <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-8">
        <div className="flex flex-col items-start gap-6 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0 w-full md:w-auto">
            <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
              Customer / QR view
            </p>
            <h1
              className="mt-1 text-2xl font-bold leading-tight text-slate-900 sm:text-3xl"
              style={{ fontFamily: 'Manrope' }}
            >
              {view.product}
            </h1>
            <p className="mt-1 max-w-xl text-sm text-slate-600">
              {view.customer_message}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <span
                data-testid="optimar-customer-traceability-status"
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${
                  resolved
                    ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                    : 'border-amber-300 bg-amber-50 text-amber-800'
                }`}
              >
                {resolved ? (
                  <CheckCircle2 className="h-3.5 w-3.5" />
                ) : (
                  <AlertTriangle className="h-3.5 w-3.5" />
                )}
                {view.traceability_status}
              </span>
              <Link
                to={verifyPath}
                target="_blank"
                rel="noopener"
                className="inline-flex items-center gap-1.5 rounded-full border border-blue-300 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-800 hover:bg-blue-100"
                data-testid="optimar-customer-open-public-link"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Open public verify page
              </Link>
            </div>
          </div>
          <div className="mx-auto flex shrink-0 flex-col items-center rounded-xl border border-slate-200 bg-white p-4 md:mx-0">
            <QRCodeSVG
              value={verifyUrl}
              size={128}
              level="M"
              data-testid="optimar-customer-qr"
            />
            <p className="mt-2 max-w-[160px] break-words text-center text-[10px] font-mono text-slate-500">
              {verifyPath}
            </p>
            <p className="mt-1 text-xs text-slate-500">Scan to verify (demo)</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="optimar-customer-fields">
        <InfoLine icon={Fish} label="Product" value={view.product} />
        <InfoLine icon={MapPin} label="Origin" value={view.origin} />
        <InfoLine icon={Award} label="Quality grade" value={view.quality_grade} />
        <InfoLine icon={Calendar} label="Production date" value={productionDate} />
        <InfoLine icon={Snowflake} label="Storage status" value={view.storage_status} />
        <InfoLine
          icon={ShieldCheck}
          label="Sustainability / compliance"
          value={view.sustainability_badges.join(' · ')}
        />
      </div>

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs leading-relaxed text-slate-600">
        Only a limited subset of batch information is exposed on the customer
        view. Full traceability (RFID linkage, manual corrections, plant
        operator data) remains available to auditors only.
      </div>
    </div>
  );
};

export default OptimarCustomerView;
